<template>
  <!-- Модальное окно создания задачи / подзадачи -->
  <Teleport to="body">
    <div
      v-if="isOpen && isFullEmbedded"
      class="entity-form-backdrop"
      @click="onDialogDismissAttempt"
    />
  </Teleport>

  <!-- Кнопка закрытия в правом верхнем углу для полной формы (паттерн entity-form-actions); на мобильном — в строке заголовка -->
  <Teleport to="body">
    <div
      v-if="isOpen && isFullEmbedded"
      class="entity-form-actions"
    >
      <VBtn
        icon="ri-close-line"
        color="primary"
        class="entity-form-action-btn"
        aria-label="Закрыть"
        @click="requestCloseModal"
      />
    </div>
  </Teleport>

  <VDialog
    v-model="isOpen"
    :width="isFullForm || smAndDown ? undefined : 750"
    :max-width="isFullForm ? fullFormLayout.maxWidth.value : 750"
    :content-class="isFullForm ? fullFormLayout.contentClass.value : 'compact-task-create-dialog entity-form-menu'"
    :z-index="isFullForm ? 2400 : 2500"
    :scrim="!isFullEmbedded"
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <!-- 1. Компактный режим (Скриншот 14) -->
    <VCard
      v-if="!isFullForm"
      class="rounded-lg elevation-4 overflow-hidden compact-create-card"
      :class="xs ? 'pa-4' : 'pa-6'"
      :style="smAndDown ? undefined : 'width: 750px; max-width: 750px;'"
    >
      <!-- Шапка компактной модалки по паттернам проекта -->
      <div class="d-flex align-center justify-space-between mb-5">
        <span class="compact-modal-title">
          {{ modalTitle }}
        </span>
        <CustomCloseButton :close-action="requestCloseModal" />
      </div>

      <div>
        <VForm ref="formRef" @submit.prevent="handleSubmit">
          <!-- Строка 1: Задача* и Крайний срок (плавающий лейбл по макету) -->
          <div class="d-flex ga-4 mb-4" :class="{ 'flex-column': xs }">
            <div class="flex-grow-1">
              <VTextField
                v-model="form.title"
                density="comfortable"
                variant="outlined"
                hide-details="auto"
                :rules="[v => !!v?.trim() || 'Обязательное поле']"
              >
                <template #label>
                  <span>Задача<sup class="required-label">*</sup></span>
                </template>
              </VTextField>
            </div>

            <div :style="xs ? undefined : 'width: 220px; flex-shrink: 0;'">
              <VMenu
                v-model="isDatePickerOpen"
                :close-on-content-click="false"
                offset="8"
                content-class="datetime-picker-menu"
              >
                <template #activator="{ props: menuProps }">
                  <VTextField
                    v-bind="menuProps"
                    :model-value="formattedDeadline"
                    label="Крайний срок"
                    density="comfortable"
                    variant="outlined"
                    hide-details="auto"
                    readonly
                    append-inner-icon="ri-calendar-line"
                    class="cursor-pointer invisible-readonly"
                    @click="isDatePickerOpen = true"
                  />
                </template>
                <VCard>
                  <DateTimePicker
                    :model-value="deadlineDateValue"
                    only-future-date
                    @save="onDeadlineSave"
                  />
                </VCard>
              </VMenu>
            </div>
          </div>

          <!-- Строка 2: Описание (VTextarea как в форме НС: rows="3", no-resize) и кнопка-скрепка справа -->
          <div class="d-flex align-start ga-3 mb-4">
            <VTextarea
              v-model="form.instructions"
              label="Описание"
              density="comfortable"
              variant="outlined"
              rows="3"
              no-resize
              hide-details="auto"
              class="flex-grow-1"
            />
            <VTooltip location="top" text="Прикрепить файл">
              <template #activator="{ props: tooltipProps }">
                <VBtn
                  v-bind="tooltipProps"
                  icon="ri-attachment-line"
                  variant="text"
                  color="secondary"
                  size="large"
                  class="flex-shrink-0 mt-1"
                  @click="triggerFileInput"
                />
              </template>
            </VTooltip>
          </div>

          <!-- Прикрепление и отображение файлов через системный FileAttach -->
          <div v-show="attachedFiles.length > 0" class="mb-4">
            <FileAttach
              ref="fileAttachRef"
              :files="attachedFiles"
              accept="*"
              @update:files="attachedFiles = $event"
            />
          </div>

          <!-- Строка 3: Исполнитель* (кликабельный инпут со стрелкой вниз, открывает EmployeeSelectionModal) -->
          <div class="mb-4">
            <VTextField
              :model-value="executorDisplayValue"
              density="comfortable"
              variant="outlined"
              readonly
              append-inner-icon="ri-arrow-down-s-line"
              class="cursor-pointer invisible-readonly"
              hide-details="auto"
              :rules="[() => !!form.responsible_employee_id || 'Выберите исполнителя']"
              @click="openExecutorSelection"
            >
              <template #label>
                <span>Исполнитель<sup class="required-label">*</sup></span>
              </template>
            </VTextField>
          </div>

          <!-- Строка 4: Свитчи «Обязательный отчет» и «Срочная» -->
          <div class="d-flex align-center flex-wrap ga-6 mb-5">
            <VSwitch
              v-model="form.is_report_required"
              label="Обязательный отчет"
              density="compact"
              color="primary"
              hide-details
            />

            <VSwitch
              v-model="form.is_high_priority"
              label="Срочная"
              density="compact"
              color="primary"
              hide-details
            />
          </div>

          <!-- Подвал Скриншота 14: Слева ссылка «Полная форма», справа ОДНА кнопка «Создать» (без кнопки Отмена!) -->
          <div class="d-flex pt-1" :class="xs ? 'flex-column ga-3' : 'align-center justify-space-between'">
            <!-- На xs ссылка становится кнопкой на всю ширину, как остальные действия формы -->
            <VBtn
              v-if="xs"
              variant="outlined"
              color="primary"
              class="text-none rounded-lg font-weight-medium"
              height="38"
              block
              @click="isFullForm = true"
            >
              Полная форма
            </VBtn>
            <span
              v-else
              class="text-primary cursor-pointer text-body-2 font-weight-medium hover-underline"
              @click="isFullForm = true"
            >
              Полная форма
            </span>

            <VBtn
              color="primary"
              class="text-none px-6 rounded-lg font-weight-medium"
              height="38"
              :block="xs"
              :loading="isLoading"
              :disabled="!isFormValid"
              @click="handleSubmit"
            >
              {{ submitButtonLabel }}
            </VBtn>
          </div>
        </VForm>
      </div>
    </VCard>

    <!-- 2. Полная двухпанельная форма (Скриншот 15, паттерн entity-form-card) -->
    <VCard
      v-else
      class="d-flex flex-column overflow-hidden"
      :class="{ 'entity-form-card h-100': isFullEmbedded }"
    >
      <div class="d-flex flex-grow-1 overflow-hidden" style="min-height: 0;">
        <!-- Левая панель: форма задачи (Скриншот 15); на мобильном — на всю ширину -->
        <div
          class="full-form-task-pane d-flex flex-column flex-grow-1 overflow-y-auto"
          :class="[xs ? 'pa-4' : 'pa-6', { 'border-e': isFullEmbedded }]"
          :style="isFullEmbedded ? 'width: 58%;' : undefined"
        >
          <!-- Иконки поля «Задача» (скрепка, бургер, закрыть на мобильном): описаны один раз, выводятся в двух местах -->
          <DefineTitleActions>
            <VTooltip location="top" text="Прикрепить файл">
              <template #activator="{ props: tooltipProps }">
                <VBtn
                  v-bind="tooltipProps"
                  icon="ri-attachment-line"
                  variant="text"
                  size="small"
                  color="secondary"
                  @click="triggerFileInput"
                />
              </template>
            </VTooltip>
            <VMenu location="bottom end" content-class="entity-form-menu">
              <template #activator="{ props: menuProps }">
                <VBtn
                  v-bind="menuProps"
                  icon="ri-menu-line"
                  variant="text"
                  size="small"
                  color="secondary"
                />
              </template>

              <VList density="compact" class="py-1 task-burger-menu">
                <!-- Отметить как срочную / Снять отметку срочной -->
                <VListItem class="cursor-pointer task-burger-menu-item" @click="togglePriority">
                  <div class="d-flex align-center ga-2">
                    <VIcon icon="ri-fire-line" size="20" class="flex-shrink-0" />
                    <span class="task-burger-menu-text">
                      {{ form.is_high_priority ? 'Убрать отметку срочности' : 'Отметить как срочную' }}
                    </span>
                  </div>
                </VListItem>

                <!-- Обязательный отчет -->
                <VListItem class="cursor-pointer task-burger-menu-item" @click="toggleReportRequired">
                  <div class="d-flex align-center ga-2">
                    <VIcon icon="ri-file-text-line" size="20" class="flex-shrink-0" />
                    <span class="task-burger-menu-text">
                      {{ form.is_report_required ? 'Снять обязательность отчета' : 'Обязательный отчет' }}
                    </span>
                  </div>
                </VListItem>
              </VList>
            </VMenu>

            <CustomCloseButton v-if="!isFullEmbedded" :close-action="requestCloseModal" />
          </DefineTitleActions>

          <!-- xs: иконки строкой над полем, равномерно — как панель кнопок в форме НС -->
          <div v-if="xs" class="d-flex align-center justify-space-evenly mb-3">
            <ReuseTitleActions />
          </div>

          <!-- 1. Поле «Задача» с подчеркиванием, скрепкой и бургером -->
          <div class="d-flex align-center pb-2 mb-4 task-title-underlined-box position-relative">
            <VTooltip v-if="form.is_high_priority" location="top" text="Срочная задача">
              <template #activator="{ props: tipProps }">
                <VIcon
                  v-bind="tipProps"
                  icon="ri-fire-line"
                  color="error"
                  size="22"
                  class="flex-shrink-0 me-2"
                />
              </template>
            </VTooltip>
            <input
              v-model="form.title"
              class="task-title-input flex-grow-1"
            />
            <div
              v-if="!form.title"
              class="task-title-placeholder-overlay"
              :class="{ 'task-title-placeholder-with-icon': form.is_high_priority }"
            >
              Задача<span class="required-label">*</span>
            </div>
            <div v-if="!xs" class="d-flex align-center ga-2 flex-shrink-0 ms-2">
              <ReuseTitleActions />
            </div>
          </div>

          <!-- 2. Описание задачи (переиспользуемый компонент по Скриншотам 7 и 8) -->
          <div class="mb-5">
            <TaskDescriptionField
              v-model="form.instructions"
              placeholder="Описание"
            />

            <!-- Список прикрепленных файлов в полной форме через FileAttach -->
            <div v-show="attachedFiles.length > 0" class="mt-3">
              <FileAttach
                ref="fullFileAttachRef"
                :files="attachedFiles"
                accept="*"
                @update:files="attachedFiles = $event"
              />
            </div>
          </div>

          <!-- Чек-листы СРАЗУ ПОД ОПИСАНИЕМ (Скриншот 12, 13) -->
          <div v-if="checklists.length > 0" class="mb-5">
            <TaskChecklist
              v-for="cl in checklists"
              :key="cl.id"
              :checklist="cl"
              :can-edit="true"
              :can-toggle="false"
              @deleted="handleChecklistDeleted"
            />
          </div>

          <!-- 3. Метаданные (Постановщик, Исполнитель, Крайний срок) -->
          <div class="d-flex flex-column ga-4 mb-6">
            <!-- Постановщик -->
            <div class="d-flex align-center" style="min-height: 36px;">
              <span class="metadata-label">Постановщик</span>
              <div class="d-flex align-center ga-2">
                <VAvatar size="32" color="primary" variant="tonal">
                  <VImg v-if="currentUser?.avatar" :src="currentUser.avatar" />
                  <span v-else class="text-caption font-weight-medium">{{ currentUserInitials }}</span>
                </VAvatar>
                <div class="d-flex flex-column">
                  <span class="text-body-2 font-weight-medium text-high-emphasis">
                    {{ currentUserNameShort }}
                  </span>
                  <span v-if="currentUserPositionName" class="text-caption text-medium-emphasis">
                    {{ currentUserPositionName }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Исполнитель (на создании: кнопка Назначить при отсутствии, аватар + ФИО + должность при выборе) -->
            <div class="d-flex align-center" style="min-height: 36px;">
              <span class="metadata-label">Исполнитель<sup class="required-label">*</sup></span>
              <div
                class="d-flex align-center ga-2 cursor-pointer hover-bg-subtle pa-1 rounded"
                @click="openExecutorSelection"
              >
                <template v-if="selectedExecutor">
                  <VAvatar size="32" class="border flex-shrink-0">
                    <VImg :src="getEmployeeAvatar(selectedExecutor, selectedExecutor.id || 'selected-executor')" />
                  </VAvatar>
                  <div class="d-flex flex-column">
                    <span class="text-body-2 font-weight-medium text-high-emphasis">
                      {{ selectedExecutorNameShort }}
                    </span>
                    <span v-if="selectedExecutor.position_name" class="text-caption text-medium-emphasis">
                      {{ selectedExecutor.position_name }}
                    </span>
                  </div>
                </template>
                <VBtn
                  v-else
                  variant="text"
                  color="primary"
                  density="comfortable"
                  class="px-2 text-none font-weight-medium"
                  prepend-icon="ri-user-add-line"
                  @click.stop="openExecutorSelection"
                >
                  Назначить
                </VBtn>
              </div>
            </div>

            <!-- Крайний срок (по скриншоту 15: иконка календаря + Без срока) -->
            <div class="d-flex align-center" style="min-height: 36px;">
              <span class="metadata-label">Крайний срок</span>
              <div>
                <VMenu
                  v-model="isFullDatePickerOpen"
                  :close-on-content-click="false"
                  offset="8"
                  content-class="datetime-picker-menu"
                >
                  <template #activator="{ props: menuProps }">
                    <div
                      v-bind="menuProps"
                      class="d-flex align-center ga-2 cursor-pointer hover-bg-subtle pa-1 rounded"
                      @click="isFullDatePickerOpen = true"
                    >
                      <VIcon icon="ri-calendar-line" size="18" color="primary" />
                      <span :class="formattedDeadline ? 'text-body-2 text-high-emphasis' : 'text-body-2 text-medium-emphasis'">
                        {{ formattedDeadline || 'Без срока' }}
                      </span>
                    </div>
                  </template>
                  <VCard>
                    <DateTimePicker
                      :model-value="deadlineDateValue"
                      only-future-date
                      @save="onFullDeadlineSave"
                    />
                  </VCard>
                </VMenu>
              </div>
            </div>
          </div>

          <!-- 4. Кнопки второго уровня (+ Подзадача, Наблюдатели, Чек-лист) -->
          <div class="d-flex ga-3 mb-6" :class="xs ? 'flex-column' : 'align-center'">
            <VBtn
              variant="outlined"
              color="secondary"
              density="comfortable"
              prepend-icon="ri-checkbox-line"
              class="text-none"
              :block="xs"
              :disabled="checklists.length > 0"
              @click="addChecklist"
            >
              Чек-лист
            </VBtn>
          </div>

          <div class="mt-auto pt-4 border-t d-flex align-center justify-end">
            <VBtn
              color="primary"
              class="text-none px-7 rounded-lg font-weight-medium"
              height="38"
              :block="xs"
              :loading="isLoading"
              :disabled="!isFormValid"
              @click="handleSubmit"
            >
              {{ submitButtonLabel }}
            </VBtn>
          </div>
        </div>

        <!-- Правая панель: системный оверлей недоступного чата с текстом из макета; на мобильном не показываем -->
        <div v-if="isFullEmbedded" class="full-form-chat-pane position-relative flex-grow-1 overflow-hidden" style="width: 42%;">
          <EntityAccessDeniedOverlay
            variant="chat"
            :image="taskChatPlaceholder"
            title="Здесь будет чат задачи"
            subtitle="Обсуждайте результаты работы и обменивайтесь документами и файлами"
          />
        </div>
      </div>
    </VCard>
  </VDialog>

  <!-- Модалка выбора исполнителя (проектный компонент) -->
  <EmployeeSelectionModal
    ref="employeeModalRef"
    :employees="apiStore.employees"
    :multiple="false"
    title="Выберите исполнителя"
    apply-label="Выбрать"
    @select="handleExecutorSelected"
  />

  <!-- Диалог подтверждения закрытия при наличии несохраненных данных -->
  <ConfirmDialog
    :is-dialog-visible="showUnsavedChangesDialog"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Данные не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Отменить"
    @confirm="handleCloseConfirm"
    @update:is-dialog-visible="showUnsavedChangesDialog = $event"
  />
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { createReusableTemplate } from '@vueuse/core'
import { useDisplay } from 'vuetify'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { useFormEmbeddedLayout } from '@/modules/chat-matrix-fast/composables/useFormEmbeddedLayout'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import DateTimePicker from '@/components/createdUi/DateTimePicker.vue'
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import EntityAccessDeniedOverlay from '@/components/createdUi/EntityAccessDeniedOverlay.vue'
import FileAttach from '@/modules/files/components/FileAttach.vue'
import TaskChecklist from '@/modules/tasks/components/TaskChecklist.vue'
import TaskDescriptionField from '@/modules/tasks/components/TaskDescriptionField.vue'
import taskChatPlaceholder from '@images/icons/task_chat_placeholder.svg?url'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import { useTasksStore } from '@/modules/tasks/stores/tasksStore'
import type { IResponsibilityListItem } from '@/modules/tasks/types/IResponsibilityListItem'
import type { ITaskChecklist } from '@/modules/tasks/types/ITaskChecklist'
import { useUserStore } from '@/modules/user/stores/userStore'
import { formatDateTime, getFullName, getInitialsFullName } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'

import type {
  ICreateTaskInitialData,
  ICreateTaskSubmitPayload,
} from '@/modules/tasks/types/ICreateTaskModalData'

export type { ICreateTaskInitialData, ICreateTaskSubmitPayload }

interface Props {
  modelValue: boolean
  parentTaskId?: number | null
  defaultDeadline?: string | null
  modalTitle?: string
  submitButtonText?: string
  initialData?: ICreateTaskInitialData | null
  customSubmit?: (payload: ICreateTaskSubmitPayload) => Promise<any>
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  parentTaskId: null,
  defaultDeadline: null,
  modalTitle: undefined,
  submitButtonText: undefined,
  initialData: null,
  customSubmit: undefined,
})

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void
  (e: 'created', task: IResponsibilityListItem): void
}>()

watch(
  () => [props.modelValue, props.defaultDeadline, props.initialData] as const,
  ([open, deadline, init]) => {
    if (open) {
      if (init) {
        if (init.title !== undefined) form.title = init.title
        if (init.instructions !== undefined) form.instructions = init.instructions
        if (init.deadline_time !== undefined) form.deadline_time = init.deadline_time
        if (init.is_high_priority !== undefined) form.is_high_priority = Boolean(init.is_high_priority)
        if (init.is_report_required !== undefined) form.is_report_required = Boolean(init.is_report_required)
        if (init.responsible_employee) {
          selectedExecutor.value = init.responsible_employee
          form.responsible_employee_id = String(init.responsible_employee.id)
        } else if (init.responsible_employee_id) {
          form.responsible_employee_id = String(init.responsible_employee_id)
        }
      }
      if (deadline && !form.deadline_time) {
        form.deadline_time = deadline
      }
    }
  },
  { immediate: true },
)

const apiStore = useApiStore()
const tasksStore = useTasksStore()
const userStore = useUserStore()

const isFullForm = ref(false)

const { xs, smAndDown } = useDisplay()

// Полная форма на весь экран с чатом — только на mdAndUp, как формы сущностей; ниже — обычная модалка
const fullFormLayout = useFormEmbeddedLayout(isFullForm)
const isFullEmbedded = fullFormLayout.isEmbedded

// Иконки поля «Задача»: на десктопе внутри подчёркнутого поля, на xs — строкой над ним
const [DefineTitleActions, ReuseTitleActions] = createReusableTemplate()
const isLoading = ref(false)
const isDatePickerOpen = ref(false)
const isExecutorModalOpen = ref(false)
const fileAttachRef = ref<InstanceType<typeof FileAttach> | null>(null)
const fullFileAttachRef = ref<InstanceType<typeof FileAttach> | null>(null)
const attachedFiles = ref<File[]>([])
const checklists = ref<ITaskChecklist[]>([])

function addChecklist() {
  if (checklists.value.length === 0) {
    checklists.value.push({
      id: Date.now(),
      title: 'Чек-лист',
      items: [],
    })
  }
}

function handleChecklistDeleted(id: number) {
  checklists.value = checklists.value.filter(c => c.id !== id)
}

const selectedExecutor = ref<any>(null)

const form = reactive({
  title: '',
  deadline_time: null as string | null,
  instructions: '',
  responsible_employee_id: '' as string,
  is_report_required: false,
  is_high_priority: false,
})

function togglePriority() {
  form.is_high_priority = !form.is_high_priority
}

function toggleReportRequired() {
  form.is_report_required = !form.is_report_required
}

const deadlineDateValue = computed<Date | null>(() => {
  if (!form.deadline_time) return null
  const d = new Date(form.deadline_time)
  return Number.isNaN(d.getTime()) ? null : d
})

const formattedDeadline = computed(() => {
  if (!form.deadline_time) return ''
  const d = new Date(form.deadline_time)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`
})

const isFullDatePickerOpen = ref(false)

function onDeadlineSave(val: Date | null) {
  isDatePickerOpen.value = false
  if (!val) {
    form.deadline_time = null
    return
  }
  form.deadline_time = val.toISOString()
}

function onFullDeadlineSave(val: Date | null) {
  isFullDatePickerOpen.value = false
  if (!val) {
    form.deadline_time = null
    return
  }
  form.deadline_time = val.toISOString()
}

const isOpen = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
})

const modalTitle = computed(() => {
  return props.modalTitle || (props.parentTaskId ? 'Создание подзадачи' : 'Создание задачи')
})

const submitButtonLabel = computed(() => {
  return props.submitButtonText || 'Создать'
})

const currentUser = computed(() => userStore.user)
const currentUserName = computed(() => {
  if (!userStore.user) return '—'
  return getFullName(userStore.user) || 'Пользователь'
})
const currentUserInitials = computed(() => {
  if (!userStore.user) return '?'
  const f = userStore.user.first_name?.[0] || ''
  const l = userStore.user.last_name?.[0] || ''
  return (l + f).toUpperCase() || '?'
})

const currentUserNameShort = computed(() => {
  if (!userStore.user) return '—'
  return getInitialsFullName(userStore.user) || 'Пользователь'
})

const currentUserPositionName = computed(() => {
  return userStore.user?.position?.name || (userStore.user as any)?.position_name || ''
})

const selectedExecutorNameShort = computed(() => {
  if (!selectedExecutor.value) return '—'
  return getInitialsFullName(selectedExecutor.value) || selectedExecutor.value.full_name || '—'
})

const executorDisplayValue = computed(() => {
  if (!selectedExecutor.value) return ''
  return getFullName(selectedExecutor.value) || selectedExecutor.value.full_name || 'Сотрудник выбран'
})

const isFormValid = computed(() => {
  return Boolean(form.title.trim() && form.responsible_employee_id)
})

const fallbackAvatars = new Map<string | number, string>()

function getEmployeeAvatar(emp?: any, fallbackKey?: string | number): string {
  if (emp?.avatar) return emp.avatar
  const id = emp?.id || fallbackKey || 'unknown'
  if (!fallbackAvatars.has(id)) {
    fallbackAvatars.set(id, getRandomAvatar())
  }
  return fallbackAvatars.get(id)!
}

const employeeModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

function openExecutorSelection() {
  employeeModalRef.value?.open()
}

function handleExecutorSelected(emp: IEmployeeTreeItem) {
  selectedExecutor.value = emp
  form.responsible_employee_id = emp.id
}

function triggerFileInput() {
  if (isFullForm.value) {
    fullFileAttachRef.value?.openDialog()
  } else {
    fileAttachRef.value?.openDialog()
  }
}

const showUnsavedChangesDialog = ref(false)

function hasChanges(): boolean {
  if (form.title.trim()) return true
  if (form.instructions.trim()) return true
  if (form.deadline_time) return true
  if (form.responsible_employee_id) return true
  if (form.is_report_required) return true
  if (form.is_high_priority) return true
  if (attachedFiles.value.length > 0) return true
  if (checklists.value.length > 0) return true
  return false
}

function requestCloseModal() {
  if (hasChanges()) {
    showUnsavedChangesDialog.value = true
  } else {
    closeModalDirectly()
  }
}

function handleCloseConfirm(confirmed: boolean) {
  showUnsavedChangesDialog.value = false
  if (confirmed) {
    closeModalDirectly()
  }
}

function closeModalDirectly() {
  isOpen.value = false
  isFullForm.value = false
  resetForm()
}

const onDialogDismissAttempt = useDialogDismissGuard(requestCloseModal, 'CreateTaskModal')

function resetForm() {
  form.title = ''
  form.deadline_time = null
  form.instructions = ''
  form.responsible_employee_id = ''
  form.is_report_required = false
  form.is_high_priority = false
  selectedExecutor.value = null
  attachedFiles.value = []
  checklists.value = []
}

async function handleSubmit() {
  if (!isFormValid.value) return

  isLoading.value = true
  try {
    const formData = new FormData()
    formData.append('title', form.title.trim())
    formData.append('instructions', form.instructions.trim())
    if (form.deadline_time) {
      formData.append('deadline_time', form.deadline_time)
    }
    formData.append('is_report_required', String(form.is_report_required))
    formData.append('is_high_priority', String(form.is_high_priority))
    formData.append('responsible_employee_ids', form.responsible_employee_id)

    for (const f of attachedFiles.value) {
      formData.append('files', f)
    }

    if (props.customSubmit) {
      const res = await props.customSubmit({
        title: form.title.trim(),
        instructions: form.instructions.trim(),
        deadline_time: form.deadline_time,
        is_report_required: form.is_report_required,
        is_high_priority: form.is_high_priority,
        responsible_employee_id: form.responsible_employee_id,
        responsible_employee: selectedExecutor.value,
        files: attachedFiles.value,
      })
      emit('created', res as any)
      closeModalDirectly()
      return
    }

    let createdTask: IResponsibilityListItem | null = null

    if (props.parentTaskId) {
      formData.append('parent_entry', String(props.parentTaskId))
      createdTask = await tasksStore.createSubtask(props.parentTaskId, formData)
    } else {
      const payload: any = attachedFiles.value.length > 0
        ? formData
        : {
            title: form.title.trim(),
            instructions: form.instructions.trim(),
            deadline_time: form.deadline_time,
            is_report_required: form.is_report_required,
            is_high_priority: form.is_high_priority,
            responsible_employee_ids: [form.responsible_employee_id],
          }
      createdTask = await tasksStore.createEntry(payload)
    }

    if (createdTask) {
      // Сохранение локальных чек-листов к созданной задаче
      if (checklists.value.length > 0 && createdTask.id) {
        for (const cl of checklists.value) {
          const newCl = await tasksStore.createChecklist(createdTask.id, cl.title)
          if (newCl && cl.items && cl.items.length > 0) {
            for (const item of cl.items) {
              const newItem = await tasksStore.createChecklistItem(createdTask.id, newCl.id, item.text)
              if (newItem && item.is_completed) {
                await tasksStore.toggleChecklistItem(createdTask.id, newCl.id, newItem.id)
              }
            }
          }
        }
      }

      emit('created', createdTask)
      closeModalDirectly()
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped lang="scss">
.compact-task-create-dialog {
  border-radius: 8px !important;
}

.metadata-label {
  width: 140px;
  flex-shrink: 0;
  white-space: pre-line;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: rgb(var(--v-theme-secondary));
}

.compact-modal-title {
  font-family: 'Inter', sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
  color: rgb(var(--v-theme-text-primary));
}

.required-label {
  color: rgb(var(--v-theme-error));
  margin-inline-start: 2px;
}

.hover-underline:hover {
  text-decoration: underline;
}

.hover-bg-subtle:hover {
  background-color: rgba(var(--v-theme-on-surface), var(--v-hover-opacity));
}

.task-title-underlined-box {
  border-bottom: 2px solid rgb(var(--v-theme-primary));
}

.task-title-input {
  font-size: 1.25rem;
  font-weight: 600;
  border: none;
  outline: none;
  background: transparent;
  color: rgb(var(--v-theme-on-surface));

  &::placeholder {
    color: rgba(var(--v-theme-on-surface), 0.4);
    font-weight: 400;
  }
}

.task-title-placeholder-overlay {
  position: absolute;
  inset-inline-start: 0;
  top: 0;
  bottom: 8px;
  display: flex;
  align-items: center;
  font-size: 1.25rem;
  font-weight: 400;
  color: rgba(var(--v-theme-on-surface), 0.4);
  pointer-events: none;
  user-select: none;
}

.task-title-placeholder-with-icon {
  inset-inline-start: 30px !important;
}

:deep(.task-burger-menu) {
  box-shadow: 0px 6px 16px rgba(46, 38, 61, 0.22) !important;
  border-radius: 6px !important;

  .task-burger-menu-item {
    min-height: 36px !important;
    padding: 6px 14px !important;
  }

  .task-burger-menu-text {
    font-family: Inter, sans-serif !important;
    font-size: 15px !important;
    font-style: normal !important;
    font-weight: 400 !important;
    line-height: 20px !important;
    color: rgb(var(--v-theme-on-surface)) !important;
    white-space: nowrap;
  }

  .v-icon {
    color: rgb(var(--v-theme-on-surface)) !important;
  }
}
</style>

